import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, ChevronRight, RefreshCw, Bell } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function SLAAlerts() {
  const navigate = useNavigate();
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSlaData();
    // Refresh every 2 minutes
    const interval = setInterval(fetchSlaData, 120000);
    return () => clearInterval(interval);
  }, []);

  const fetchSlaData = async () => {
    try {
      const res = await api.getSlaDashboard();
      setSlaData(res.data);
    } catch (err) {
      console.error("Failed to fetch SLA data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="p-4 text-center text-amber-600">
          <RefreshCw className="h-5 w-5 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!slaData) return null;

  const hasIssues = slaData.sla_breached > 0 || slaData.sla_warning > 0;

  return (
    <Card className={`${hasIssues ? 'border-red-200 bg-red-50/30' : 'border-green-200 bg-green-50/30'}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasIssues ? (
              <AlertTriangle className="h-4 w-4 text-red-600" />
            ) : (
              <Bell className="h-4 w-4 text-green-600" />
            )}
            <span className={hasIssues ? 'text-red-700' : 'text-green-700'}>
              SLA Status
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-xs ${slaData.sla_breached > 0 ? 'border-red-300 text-red-700 bg-red-100' : 'border-gray-200'}`}>
              {slaData.sla_breached} Breached
            </Badge>
            <Badge variant="outline" className={`text-xs ${slaData.sla_warning > 0 ? 'border-amber-300 text-amber-700 bg-amber-100' : 'border-gray-200'}`}>
              {slaData.sla_warning} At Risk
            </Badge>
            <Badge variant="outline" className="text-xs border-green-300 text-green-700 bg-green-100">
              {slaData.sla_ok} OK
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {slaData.escalation_needed?.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-red-600 font-medium">Escalation Needed:</p>
            {slaData.escalation_needed.slice(0, 3).map((ticket) => (
              <div
                key={ticket.ticket_id}
                onClick={() => navigate(`/tickets/managed/${ticket.ticket_id}`)}
                className="flex items-center justify-between p-2 bg-white rounded border border-red-200 cursor-pointer hover:bg-red-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">#{ticket.ticket_number}</span>
                    <Badge className={`text-[10px] px-1 py-0 ${
                      ticket.priority === 'Critical' ? 'bg-red-500' :
                      ticket.priority === 'High' ? 'bg-orange-500' : 'bg-blue-500'
                    }`}>
                      {ticket.priority}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600 truncate">{ticket.summary}</p>
                  <p className="text-[10px] text-red-600">{ticket.reason}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="h-3 w-3 mr-1" />
                    {Math.round(ticket.age_minutes / 60)}h
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </div>
              </div>
            ))}
            {slaData.escalation_needed.length > 3 && (
              <p className="text-xs text-center text-red-600">
                +{slaData.escalation_needed.length - 3} more needing attention
              </p>
            )}
          </div>
        ) : slaData.warning_tickets?.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-amber-600 font-medium">At Risk:</p>
            {slaData.warning_tickets.slice(0, 2).map((ticket) => (
              <div
                key={ticket.ticket_id}
                onClick={() => navigate(`/tickets/managed/${ticket.ticket_id}`)}
                className="flex items-center justify-between p-2 bg-white rounded border border-amber-200 cursor-pointer hover:bg-amber-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium">#{ticket.ticket_number}</span>
                  <p className="text-xs text-gray-600 truncate">{ticket.summary}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-2">
            <p className="text-sm text-green-600">✓ All tickets within SLA</p>
            <p className="text-xs text-gray-500">{slaData.total_open} open tickets</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
